// apps/career/contexts/AuthContext.tsx
'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import type { AuthContextType, CareerUser, UserRole, LoginResponse } from '@/types/auth.types';
import ssoClient2 from '@/../../packages/api/src/ssoClient2';
import {logoutCustomer} from '@/../../packages/utils/src/logout-helper';
import { handle401 } from '@/../../packages/api/src';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<CareerUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();
  const pathname = usePathname();

  const getUserRole = useCallback((userType: number): UserRole => {
    switch (userType) {
      case 0: return 'admin';
      case 1: return 'career';
      case 2: return 'client';
      default: return 'admin';
    }
  }, []);

  // ✅ استفاده از Route Handler به جای درخواست مستقیم
  const fetchUserDetails = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      
      // ✅ استفاده از Route Handler (نه مستقیم به بک‌اند!)
      const response = await fetch('/api/auth/details', {
        method: 'POST',
        credentials: 'include', // ✅ برای ارسال کوکی‌ها
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      // if (response.status === 401) {
      //   handle401()
      // }
      if (!response.ok || !data?.success) {
        // console.warn('⚠️ [AuthContext] User not authenticated:', data.message);
        setUser(null);
        return;
      }

      console.log('✅ [AuthContext] User details fetched:', data.user);
      setUser(data.user);
      
    } catch (error) {
      console.error('❌ [AuthContext] Fetch error:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  const login = useCallback(async (userName: string, password: string): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `/api/auth/admin-login?userName=${encodeURIComponent(userName)}&password=${encodeURIComponent(password)}`,
        { 
          method: 'POST', 
          credentials: 'include' 
        }
      );
      
      const data: LoginResponse = await response.json();

      if (!response.ok || !data.success || !data.user) {
        throw new Error(data.message || 'Login failed');
      }

      // ابتدا آپدیت وضعیت کاربر در استیت
      setUser(data.user);
      
      // سپس فراخوانی جزئیات برای اطمینان از سینک بودن کوکی‌ها
      await fetchUserDetails();
      
      const locale = pathname.split('/')[1] || 'en';
      const role = getUserRole(data.user.userType);
      
      const targetPath = role === 'admin' ? 'dashboard' : `${role}/dashboard`;
      router.push(`/${locale}/${targetPath}`);
      router.refresh();
      
    } catch (error) {
      console.error('❌ [AuthContext] Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [router, pathname, getUserRole, fetchUserDetails]);

  const logout =  async () => {
    await logoutCustomer({
      redirect: true,
      redirectTo: '/'
    });
    setUser(null);
  };
  

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    role: user ? getUserRole(user.userType) : null,
    login,
    logout,
    refreshUser: fetchUserDetails,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within AuthProvider');
  return context;
}